<template>
  <div :class="$style.mainContainer">
    <v-dialog
      v-model="resetDialog"
      width="500">
      <h6 :class="$modals.modalTitle">
        Are you sure you want to reset all the information in the fields?
      </h6>
      <div :class="$modals.modalButtons">
        <app-button text="Cancel"
                    button-style="default"
                    :class="$modals.modalBtn"
                    @click="closeModal"/>
        <app-button text="Reset"
                    button-style="accent"
                    :class="$modals.modalBtn"
                    @click="reset"/>
      </div>
    </v-dialog>


    <v-stepper :class="$style.stepperContainer"
               v-model="currentStep">
      <v-stepper-header :class="$style.stepperHeader">

        <template v-for="step in formSteps">
          <v-stepper-step :complete="currentStep > step.number"
                          :key="step.number"
                          :style="{ width: stepWidth + '%'}"
                          :step="step.number">
            {{ step.title }}
          </v-stepper-step>
        </template>

      </v-stepper-header>

      <v-stepper-items :class="$style.stepperContent">
        <slot name="stepperContent"/>

        <div :class="$style.stepperFooter">

          <template v-if="currentStep === 1">
            <div :class="$style.overviewButtons">

              <app-button v-if="previous !== 'welcome'"
                          text="Previous Section"
                          @click="previousSection"
                          button-style="default"
                          :class="$style.backBtn">
                <app-svg-icon name="ic_back"
                              :class="$style.backIcon"/>
              </app-button>

              <app-button text="Get Started"
                          button-style="accent"
                          @click="getStarted"
                          :class="$style.continueBtn" />
            </div>
          </template>

          <template v-else>
            <div>
              <template v-if="resetBtn && !disableResetButton">
                <button :class="$style.resetBtn"
                        @click="openModal">Reset Form</button>
              </template>
            </div>

            <div>
             <app-button text="Back"
                          button-style="default"
                          @click="prevStep"
                          :class="$style.backBtn">
                <app-svg-icon name="ic_back"
                              :class="$style.backIcon"/>
              </app-button>

              <app-button v-if="currentStep !== numberOfSteps"
                          text="Continue"
                          button-style="accent"
                          :disabled="getLoadingStatus"
                          @click="nextStep"
                          :class="$style.continueBtn">
                <app-svg-icon name="ic_next"
                              :class="$style.nextIcon"/>
              </app-button>

              <span v-else-if="isLastSection">
                <app-button text="Save"
                            button-style="accent"
                            @click="saveWithoutProceed"
                            :class="$style.saveBtn"/>

                <app-button text="Analyze Form"
                            button-style="accent"
                            @click="saveAndProceed"
                            :class="$style.continueBtn"/>
              </span>


              <app-button v-else
                          text="Next Section"
                          button-style="accent"
                          @click="nextStep"
                          :class="$style.continueBtn">
                <app-svg-icon name="ic_next"
                              :class="$style.nextIcon"/>
              </app-button>
            </div>
          </template>

        </div>
      </v-stepper-items>

    </v-stepper>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { USER_ACCESS_TYPES } from '@/constants';

export default {
  props: {
    formSteps: {
      type: Array,
      required: true,
    },
    currentStep: {
      type: Number,
      required: true,
    },
    isCurrentFormValid: {
      type: Boolean,
      default: false,
    },
    previous: {
      type: String,
      required: true,
    },
    resetBtn: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      resetDialog: false,
    };
  },
  methods: {
    /* Emit event when user clicks nextStep button in any of the components */
    nextStep() {
      this.$emit('nextStep', this.currentStep);
    },
    /* Emit event when user clicks prevStep button in any of the components */
    prevStep() {
      this.$emit('prevStep', this.currentStep);
    },
    /* Emit event when user clicks Save button in the last form section */
    saveWithoutProceed() {
      this.$emit('saveWithoutProceed');
    },
    /* Emit event when user clicks Analyze Form button in the last form section */
    saveAndProceed() {
      this.$emit('saveAndProceed');
    },
    /* Emit event when user clicks setStarted button in Overview subsection */
    getStarted() {
      this.$emit('nextStepDone', this.currentStep);
    },
    /* Come back to a previous section */
    previousSection() {
      if (this.previous === 'business' && this.getAppAccessType !== USER_ACCESS_TYPES.Business) {
        this.$router.push({ name: 'assets' });
        return;
      }
      this.$router.push({
        params: { id: this.getCPAId },
        name: this.previous,
      });
    },
    /* Emit reset event that should be transmitted
    into the component with the form at the current step */
    reset() {
      this.$emit('resetForm');
      this.closeModal();
    },
    /* Open dialog for submitting reset */
    openModal() {
      this.resetDialog = true;
    },
    /* Close dialog */
    closeModal() {
      this.resetDialog = false;
    },
  },
  computed: {
    ...mapGetters([
      'getAppAccessType',
      'getAddressLoadingStatus',
      'getZipCodeLoading',
      'getPersonalEditLimit',
      'getAddressEditLimit',
      'getCPAId',
    ]),
    /* Get number of steps */
    numberOfSteps() {
      return this.formSteps.length;
    },
    /* Compute width of one step in percents depending on amount of steps
    so that all steps were of the same width */
    stepWidth() {
      return 100 / this.numberOfSteps;
    },
    isLastSection() {
      return this.currentStep === this.numberOfSteps && this.previous === 'business';
    },
    getLoadingStatus() {
      return this.getZipCodeLoading;
    },
    disableResetButton() {
      const personalStep = 2;
      const addressStep = 4;
      const isProfileSection = this.previous === 'welcome';

      if (isProfileSection) {
        if (this.currentStep === personalStep && this.getPersonalEditLimit) return true;
        if (this.currentStep === addressStep && this.getAddressEditLimit) return true;

        return false;
      }

      return false;
    },
  },
};
</script>

<style src="@/styles/modals.scss" lang="scss" module="$modals"></style>
<style lang="scss" module>
@import "@/styles/variables.scss";

.mainContainer {
  width: 100%;
}

.stepperContainer {
  height: 100%;
  display: flex;
  flex-direction: column;
  /* Overriding Vuetify style */
  background-color: $main-background-color !important;

  .stepperHeader {
    background-color: $white-color;
    padding: 0 25px;
    height: auto;
    position: fixed;
    top: 0;
    left: 250px;
    z-index: 10;
    width: calc(100% - 250px);
  }

  .stepperContent {
    flex-grow: 1;
  }
}

.stepperFooter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: $white-color;
  border-top: 2px solid $light-grey;
  padding: 20px 24px 20px 60px;
  position: fixed;
  left: 250px;
  bottom: 0;
  width: calc(100% - 250px);

  .resetBtn  {
    font-family: $link-font;
    font-size: 1.7rem;
    line-height: 2.1rem;
    color: $left-sidebar-color;
    border-bottom: 1px solid $left-sidebar-color;
    outline: none;
    transition: all 0.3s ease-in;

    &:hover,
    &focus,
    &:active {
      border-color: transparent;
    }
  }

  .saveBtn {
    padding: 11px 80px;
    margin-right: 23px;
  }

  .continueBtn {
    padding: 11px 50px;

    .nextIcon {
      margin-left: 16px;
    }
  }

  .backBtn {
    padding: 11px 67px;
    margin-right: 23px;

    .backIcon {
      margin-right: 16px;
    }
  }

  .overviewButtons {
    width: 100%;
    display: flex;
    justify-content: center;

    .continueBtn {
      padding: 11px 90px;
    }

    .backBtn {
      padding: 11px 55px;
    }
  }
}

</style>

<style lang="scss">
@import "@/styles/variables.scss";

.theme--light.v-stepper .v-stepper__header {
  height: auto;
  z-index: 1;

  .v-stepper__step {
    flex-direction: column;
    padding: 16px 8px 12px;
    position: relative;

    .v-stepper__step__step {
      min-width: 15px;
      width: 15px;
      height: 15px;
      margin-right: 0;
      font-size: 0;
      position: relative;
      z-index: 5;

      &.primary {
        /* Overriding Vuetify style */
        background-color: $left-sidebar-color !important;

        svg {
          display: none;
        }
      }

      .fa-check {
        display: none;
      }
    }

    &:not(.v-stepper__step--active):not(.v-stepper__step--complete):not(.v-stepper__step--error) {

      .v-stepper__step__step {
        background-color: $stepper-color;
      }
    }

    .v-stepper__label {
      font-family: $title-font;
      font-weight: 700;
      font-size: 1.1rem;
      line-height: 1.2rem;
      color: $tooltip-background-color;
      opacity: 0.5;
      margin-top: 13px;
      text-align: center;
    }

    &.v-stepper__step--complete .v-stepper__label,
    &.v-stepper__step--active .v-stepper__label {
      opacity: 1;
    }

    &::before,
    &::after {
      content: '';
      position: absolute;
      height: 4px;
      width: calc(50% + 4px);
      top: 22px;
      border-radius: 2px;
      background-color: $stepper-color;
    }

    &::before {
      left: -2px;
    }

    &::after {
      right: -2px;
    }

    &.v-stepper__step--active::before,
    &.v-stepper__step--complete::before,
    &.v-stepper__step--complete::after,
    &:last-child.v-stepper__step--active::after{
      background-color: $left-sidebar-color;
    }
  }
}

.v-stepper  {

  .v-stepper__items {
    display: flex;
  }

  .v-stepper__content {
    padding: 0;
    display: flex;
    flex-direction: column;
    z-index: 0;

    .v-stepper__wrapper {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      margin-top: 80px;
      margin-bottom: 90px;
      max-height: calc(100vh - 170px);
      overflow-y: auto;
    }
  }
}

.v-dialog {
  padding: 20px 30px;
  background-color: $white-color;
  text-align: center;
}

</style>
